// lib/chatTranslate.js
// Free, on-device chat translation using Chrome's built-in Translator +
// LanguageDetector APIs (Chrome / Edge desktop + Android Chrome). Everything
// runs on-device — nothing is sent to any server, so it respects the chat's
// end-to-end encryption. Where the APIs aren't available (Safari/Firefox), all
// functions no-op and the caller simply shows the original text unchanged.
//
// Two safeguards keep us under Chrome's usage limits ("translation service
// count exceeded"): we (1) cache each message's result so it's translated once,
// and (2) serialize the calls through a small concurrency gate so a chat full
// of messages doesn't fire dozens of translations at the same instant.

const translatorCache = new Map();  // `${src}:${tgt}` -> Promise<Translator|null>
const resultCache = new Map();      // `${tgt}::${text}` -> {text, sourceLang}|null
let detectorPromise = null;

export function translateSupported() {
  return typeof self !== 'undefined' && 'Translator' in self && 'LanguageDetector' in self;
}

const norm = (l) => String(l || '').split('-')[0].toLowerCase();

// Chrome warns that its detector is unreliable for very short chat messages.
// Use high-signal script/character hints before asking the statistical model.
// These are intentionally conservative: a wrong source language is worse than
// leaving the original message visible.
function detectLanguageHint(text) {
  const value = String(text || '').trim();
  if (!value) return null;

  // Scripts that map reliably to a Chrome-supported language. Shared scripts
  // such as Arabic, Cyrillic and Han are left to Chrome's detector.
  if (/[\u0980-\u09FF]/u.test(value)) return 'bn';
  if (/[\u0370-\u03FF]/u.test(value)) return 'el';
  if (/[\u0590-\u05FF]/u.test(value)) return 'he';
  if (/[\u3040-\u30FF]/u.test(value)) return 'ja';
  if (/[\uAC00-\uD7AF]/u.test(value)) return 'ko';
  if (/[\u0C80-\u0CFF]/u.test(value)) return 'kn';
  if (/[\u0B80-\u0BFF]/u.test(value)) return 'ta';
  if (/[\u0C00-\u0C7F]/u.test(value)) return 'te';
  if (/[\u0E00-\u0E7F]/u.test(value)) return 'th';

  // Devanagari (Hindi/Marathi). Prefer Hindi unless the detector has enough
  // context to identify Marathi; recognize strong Marathi-only signals first.
  if (/[\u0900-\u097F]/u.test(value)) {
    if (/[ळऱ]|\b(आहे|नाही|काय|तुम्ही|माझे|धन्यवाद)\b/u.test(value)) return 'mr';
    return 'hi';
  }

  // Vietnamese-specific letters and tone-marked vowels.
  if (/[ăâđêôơưĂÂĐÊÔƠƯ]|[àáảãạằắẳẵặầấẩẫậèéẻẽẹềếểễệìíỉĩịòóỏõọồốổỗộờớởỡợùúủũụừứửữựỳýỷỹỵ]/iu.test(value)) return 'vi';

  // Portuguese-specific short-chat signals. ç alone is not sufficient, but
  // in combination with common Portuguese words it is a useful strong hint.
  if (/\b(voc[eê]|n[aã]o|est[aá]|obrigad[oa]|licen[cç]a|com|tudo|bem|amor|ol[aá])\b/iu.test(value)
      && /[ãõçáéíóúâêôà]/iu.test(value)) return 'pt';

  return null;
}

// ---- concurrency gate (max 2 in flight) --------------------------------------
let active = 0;
const waiters = [];
const MAX_CONCURRENT = 2;
function gate(fn) {
  return new Promise((resolve) => {
    const run = async () => {
      active++;
      try { resolve(await fn()); }
      finally { active--; const next = waiters.shift(); if (next) next(); }
    };
    if (active < MAX_CONCURRENT) run(); else waiters.push(run);
  });
}

async function getDetector() {
  if (!detectorPromise) {
    // eslint-disable-next-line no-undef
    const avail = LanguageDetector.availability ? await LanguageDetector.availability() : 'available';
    if (avail === 'unavailable') return null;
    // eslint-disable-next-line no-undef
    detectorPromise = LanguageDetector.create().catch(() => null);
  }
  return detectorPromise;
}

export async function detectLanguage(text) {
  if (!translateSupported() || !text) return null;
  try {
    const hint = detectLanguageHint(text);
    if (hint) return hint;
    const detector = await getDetector();
    if (!detector) return null;
    const results = await detector.detect(text);
    return results && results[0] ? results[0].detectedLanguage : null;
  } catch {
    return null;
  }
}

async function getTranslator(src, tgt) {
  const key = `${src}:${tgt}`;
  if (translatorCache.has(key)) return translatorCache.get(key);
  const p = (async () => {
    try {
      // eslint-disable-next-line no-undef
      const avail = await Translator.availability({ sourceLanguage: src, targetLanguage: tgt });
      if (avail === 'unavailable') return null;
      // eslint-disable-next-line no-undef
      return await Translator.create({ sourceLanguage: src, targetLanguage: tgt });
    } catch {
      return null;
    }
  })();
  translatorCache.set(key, p);
  const translator = await p;
  // A failed creation is often temporary while Chrome prepares/downloads a
  // language pack. Do not retain that failure for the lifetime of the page.
  if (!translator) translatorCache.delete(key);
  return translator;
}

// Returns { text, sourceLang } when a translation was produced, else null.
// Cached per (targetLang, text) so each message translates at most once.
export async function translateText(text, targetLang) {
  if (!translateSupported() || !text || !targetLang) return null;
  const tgt = norm(targetLang);
  const key = `${tgt}::${text}`;
  if (resultCache.has(key)) return resultCache.get(key);

  const outcome = await gate(async () => {
    const src = await detectLanguage(text);
    // Detection/model readiness can be transient, so only cache the definite
    // same-language case. The component can retry the other cases shortly.
    if (!src) return { cache: false, value: null };
    if (norm(src) === tgt) return { cache: true, value: null };
    const translator = await getTranslator(norm(src), tgt);
    if (!translator) return { cache: false, value: null };
    try {
      const out = await translator.translate(text);
      const value = out && out !== text ? { text: out, sourceLang: norm(src) } : null;
      return { cache: true, value };
    } catch {
      // Transient (e.g. count-exceeded / model still downloading) — don't cache,
      // so it can retry on a later render.
      return { cache: false, value: null };
    }
  });

  if (outcome.cache) resultCache.set(key, outcome.value);
  return outcome.value;
}
